<script setup>
import { ref, watch, computed, inject } from 'vue'
import { useOperationsStore } from '@/store/operations.js'
import { useRoute } from 'vue-router'
import LogFilter from '../../components/LogFilter.vue'
import { useConfirm } from 'primevue/useconfirm'

const route = useRoute()
const confirm = useConfirm()
const loading = computed(() => { return operationsStore.loadingStuff })
const windowIsTooSmall = inject('windowTooSmall')
const operationsStore = useOperationsStore()
const showDetailModal = ref(false)
const tabs = ref([])
const tempFilters = computed(() => {
  return { ...operationsStore.filters }
})
const op = ref()
const toggle = (event) => {
  op.value.toggle(event)
}
const applyFilter = async () => {
  Object.keys(tempFilters.value).forEach(k => { operationsStore.filters[k] = tempFilters.value[k] })
  await operationsStore.fetchOperationsQueue()
}

const confirmDelete = (data) => {
  confirm.require({
    group: 'confirm-deletion',
    header: `Delete ${data.type} on ${data.object} operation`,
    message: data,
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    rejectProps: {
      label: 'Cancel',
      severity: 'secondary',
      outlined: true
    },
    acceptProps: {
      label: 'Delete',
      severity: 'danger'
    },
    accept: async () => {
      await operationsStore.deleteOperation(data.id)
    }
  })
}

watch(() => route, operationsStore.fetchOperationsQueue, { immediate: true })

const openDetailModal = (operationQueue) => {
  tabs.value = []
  tabs.value.push({
    title: 'Data',
    content: operationQueue.data,
    value: '0'
  })
  if (['Failed', 'Skipped'].includes(operationQueue.status)) {
    tabs.value.push({
      title: 'Error',
      content: operationQueue.errordetails,
      id: operationQueue.id,
      showRetry: operationQueue.status === 'Failed',
      value: '1'
    })
  }
  showDetailModal.value = true
}

const mapStatusIcon = (status) => {
  if (['Processing'].includes(status)) return 'pi pi-spin pi-spinner'
  return ''
}

const mapStatusSeveriy = (status) => {
  if (status === 'Completed') return 'success'
  if (status === 'Failed') return 'danger'
  if (['Processing', 'New'].includes(status)) return 'info'
  return 'warn'
}

const mapStatusValue = (status) => {
  if (status === 'Completed') return 'OK'
  if (status === 'Failed') return 'KO'
  return status
}

const retryOperation = async (operationQueueId) => {
  operationsStore.retryOperation(operationQueueId)
  showDetailModal.value = false
}

const selectedTab = ref('0')
</script>

<template>
  <ConfirmDialog group="confirm-deletion">
    <template #message="slotProps">
      <div class="flex flex-col items-center w-full gap-4 border-b border-surface-200 dark:border-surface-700">
        <i
          :class="slotProps.message.icon"
          class="!text-6xl text-primary-500"
        />
        <p>Are you sure you want to delete the  <b>{{ slotProps.message.message.type }}</b> operation on  <b>{{ slotProps.message.message.object }}</b> object?</p>
        <p>This operation <b><u>CANNOT</u></b> be undone.</p>
        <br>
      </div>
    </template>
  </ConfirmDialog>
  <Dialog
    v-model:visible="showDetailModal"
    :position="windowIsTooSmall ? 'center' : 'right'"
    :dismissable-mask="true"
    content-style="overflow-y: hidden; display: flex; flex-direction: column"
    modal
    header="Operation Queue Detail"
    :style="{maxWidth: windowIsTooSmall ? '90vw' : '60vw', minWidth: '35vw', maxHeight: windowIsTooSmall ? '90vh' : '60vh' }"
  >
    <Tabs
      v-model:value="selectedTab"
      class="max-h-full flex min-h-0"
    >
      <TabList class="top-0">
        <Tab
          v-for="tab in tabs"
          :key="tab.title"
          :value="tab.value"
        >
          {{ tab.title }}
        </Tab>
      </TabList>
      <TabPanels class="flex flex-grow min-h-0">
        <TabPanel
          v-for="tab in tabs"
          :key="tab.content"
          :value="tab.value"
          class="w-full flex flex-col"
        >
          <highlightjs
            language="json"
            :code="JSON.stringify(tab.content, null, 2)"
            class="overflow-y-auto h-auto"
          />
        </TabPanel>
      </TabPanels>
    </Tabs>
    <template
      v-if="tabs.length > 1 && tabs[1].showRetry"
      #footer
    >
      <Button
        label="Retry"
        @click="retryOperation(tabs[1].id)"
      />
    </template>
  </Dialog>
  <div class="card">
    <LogFilter
      v-if="!windowIsTooSmall"
      v-model="operationsStore.filters"
    />
    <Divider v-if="!windowIsTooSmall" />
    <div
      v-if="loading"
      class="flex flex-1 w-full items-center justify-center"
    >
      <ProgressSpinner />
    </div>
    <div
      v-else
      class="flex-1 overflow-y-auto px-0 -mx-4 -mb-4 md:mb-0 md:mx-0 md:px-10 md:pb-4 rounded-lg bg-[--p-content-background]"
    >
      <DataTable
        v-if="!windowIsTooSmall"
        selection-mode="single"
        :value="operationsStore.operationsQueue"
        table-style="min-width: 50rem; height: 100%; width: 100%"
        scrollable
        scroll-height="flex"
        :virtual-scroller-options="{ itemSize: 47, numToleratedItems: 50 }"
        @row-select="openDetailModal($event.data)"
      >
        <template #empty>
          No operations found
        </template>
        <Column
          style="width: 150px"
          field="code"
          header="Status"
        >
          <template #body="{ data }">
            <Tag
              :icon="mapStatusIcon(data.status)"
              :severity="mapStatusSeveriy(data.status)"
              :value="mapStatusValue(data.status)"
            />
          </template>
        </Column>
        <Column
          field="object"
          header="Object"
        />
        <Column
          field="type"
          header="Type"
          style="width: 300px;"
        />
        <Column
          field="createdby"
          header="Created By"
          style="width: 300px;"
        />
        <Column
          field="createddate"
          header="Created Date"
          style="width: 200px;"
        >
          <template #body="{ data }">
            {{ new Date(data.createddate).toLocaleString([], {year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit'}) }}
          </template>
        </Column>
        <Column header="Actions">
          <template #body="{ data }">
            <div class="flex flex-row justify-center w-30">
              <Button
                v-if="['Failed', 'Skipped'].includes(data.status)"
                icon="pi pi-trash"
                rounded
                severity="danger"
                text
                @click="confirmDelete(data)"
              />
            </div>
          </template>
        </Column>
      </DataTable>
      <DataTable
        v-else
        selection-mode="single"
        :value="operationsStore.operationsQueue"
        table-style="width: 100%"
        scrollable
        scroll-height="flex"
        :virtual-scroller-options="{ itemSize: 79, numToleratedItems: 50 }"
        @row-select="openDetailModal($event.data)"
      >
        <template #empty>
          No operations found
        </template>
        <Column
          field="createddate"
          sortable
        >
          <template #header>
            <div class="flex flex-row w-full items-center justify-between">
              <b>Operations</b>
              <Button
                type="button"
                icon="pi pi-filter"
                variant="text"
                @click="toggle"
              />
              <Popover ref="op">
                <LogFilter v-model="tempFilters" />
                <div class="flex flex-row justify-end pt-3">
                  <Button
                    label="Apply"
                    @click="applyFilter()"
                  />
                </div>
              </Popover>
            </div>
          </template>
          <template #body="{ data }">
            <div class="flex items-center justify-between w-full">
              <div class="flex flex-col items-start gap-1">
                <div class="flex flex-row items-center gap-2">
                  <b>{{ data.object + ' - ' + data.type }}</b>
                </div>
                <div class="flex flex-row items-center gap-2 w-full">
                  <i
                    class="pi pi-clock"
                  />
                  <div class="flex flex-row items-center h-full">
                    {{ new Date(data.createddate).toLocaleString() }}
                  </div>
                </div>
                <div class="flex flex-row items-center gap-2 w-full">
                  <i
                    class="pi pi-user"
                  />
                  <div class="flex flex-row items-center h-full">
                    {{ data.createdby }}
                  </div>
                </div>
              </div>
              <Tag
                :icon="mapStatusIcon(data.status)"
                :severity="mapStatusSeveriy(data.status)"
                :value="mapStatusValue(data.status)"
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
</template>
<style scoped>
.p-tabpanels {
  flex-grow: 1;
}
</style>
